<template>
  <div class="compact-collapsible-section">
    <button
      class="compact-collapsible-section-toggle"
      :aria-expanded="expanded"
      @click="$emit('toggle')"
    >
      <ChevronRightIcon
        class="compact-collapsible-section-chevron"
        :class="{ expanded }"
      />
      <span
        class="compact-collapsible-section-label"
        :class="`compact-collapsible-section-label--${labelStyle}`"
        >{{ label }}</span
      >
    </button>
    <slot v-if="expanded" />
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";

type LabelStyle = "caption" | "body";

export default defineComponent({
  name: "CompactCollapsibleSection",
  components: { ChevronRightIcon },
  props: {
    expanded: { type: Boolean, required: true },
    label: { type: String, required: true },
    labelStyle: {
      type: String as PropType<LabelStyle>,
      default: "caption",
    },
  },
  emits: {
    toggle: () => true,
  },
});
</script>

<style scoped lang="scss">
.compact-collapsible-section {
  display: flex;
  flex-direction: column;
}

.compact-collapsible-section-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  border: none;
  background: none;
  border-radius: $border-radius-s;
  text-align: start;
  width: 100%;
  font-family: inherit;
  @include transition(background-color 100ms ease-out, color 100ms ease-out);

  @include themed() {
    color: t($grey-60);

    &:hover {
      background-color: t($bg-hover);
      color: t($primary);
    }
  }
}

.compact-collapsible-section-chevron {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  @include transition(transform 0.15s ease-out);

  &.expanded {
    transform: rotate(90deg);
  }
}

.compact-collapsible-section-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &--caption {
    @extend %font-caption-regular;
  }

  &--body {
    @extend %font-body-regular;
    font-size: 13px;
  }
}
</style>
